/* eslint-disable react/jsx-no-target-blank */
import { Form, Formik, Field } from 'formik';
import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Flex, Link, Text } from 'rebass/styled-components';
import {
  FormField,
  Button,
  Checkbox,
  FormFile,
  MessageBlock,
  Message,
  FormLabel,
  Select,
  FormInput,
  FormError,
} from '@getro/rombo';
import getConfig from 'next/config';
import { currentJobSelector, jobApplicationSubmitSelector } from '../../redux/selectors/jobs';
import { makeSelectIsLoading } from '../../redux/selectors/loading';
import { formSchema } from './formSchema';
import { loadJobApplicationSubmitted } from '../../redux/actions/jobs';
import { NetworkSchema } from '../../schema/network';
import usePrivacyPolicyUrl from '../../hooks/usePrivacyPolicyUrl';
import { useFileUpload } from './hooks/useFileUpload';

const { publicRuntimeConfig } = getConfig();

export const OnBoardApplication = ({ network }) => {
  const { apiV2BaseUrl: baseURL } = publicRuntimeConfig;
  const dispatch = useDispatch();
  const {
    done: savingJobDone,
    error: savingError,
    errorStatus: savingErrorStatus,
  } = useSelector(jobApplicationSubmitSelector);
  const { id: jobId, locations = [], organization } = useSelector(currentJobSelector);
  const { privacyPolicyUrl } = usePrivacyPolicyUrl(network);
  const savingJob = useSelector(makeSelectIsLoading('jobApplicationSubmitted'));

  const showUSWorkAuthorization = useMemo(
    () => locations?.some((location) => /usa|United States/gim.test(location.name)),
    [locations],
  );

  const validate = (values) => {
    const errors = {};

    if (!values.usVisaSponsorship && showUSWorkAuthorization) {
      errors.usVisaSponsorship = 'Visa status is required';
    }

    if (!values.usWorkAuthorization && showUSWorkAuthorization) {
      errors.usWorkAuthorization = 'Work Authorization is required';
    }

    return errors;
  };

  const handleSubmit = async (values) => {
    dispatch(
      loadJobApplicationSubmitted({
        network,
        ...values,
        cv: values.cvUrl[0],
        jobId,
        organizationId: organization.id,
        usWorkAuthorization: showUSWorkAuthorization ? values.usWorkAuthorization.value : null,
        usVisaSponsorship: showUSWorkAuthorization ? values.usVisaSponsorship.value : null,
      }),
    );
  };

  const { uploadFile, resumeError, handleFileChange } = useFileUpload({ network, baseURL });

  return (
    <Formik
      enableReinitialize
      initialValues={{
        cvUrl: [],
        usWorkAuthorization: { value: 'no', label: 'No' },
        usVisaSponsorship: { value: 'no', label: 'No' },
        linkedinUrl: '',
        email: '',
        firstName: '',
        lastName: '',
        termsOfService: false,
      }}
      onSubmit={handleSubmit}
      validationSchema={formSchema}
      validate={validate}
    >
      {({ values, form, errors, touched, setFieldValue }) => (
        <Form
          sx={{
            label: {
              fontSize: '16px',
            },
          }}
        >
          <Box mb={4} mt="80px" pb={3} sx={{ borderBottom: '1px solid', borderColor: '#DEE4ED' }}>
            <Text color="text.dark" fontSize={22} as="h2">
              Apply for this job
            </Text>
          </Box>
          {!savingJobDone[jobId] && (
            <>
              <Flex flexDirection="column" sx={{ rowGap: 4, '& > div:not(:last-child)': { marginBottom: 0 } }}>
                <Flex flexDirection={['column', 'row']} sx={{ gap: 4 }}>
                  <Box width={['100%', '50%']}>
                    <Field
                      type="text"
                      name="firstName"
                      id="firstName"
                      component={FormInput}
                      label="First name"
                      labelExtra="Required"
                      size="large"
                      optional
                    />
                  </Box>
                  <Box width={['100%', '50%']}>
                    <Field
                      type="text"
                      name="lastName"
                      id="lastName"
                      component={FormInput}
                      label="Last name"
                      optionalText="Required"
                      size="large"
                      optional
                    />
                  </Box>
                </Flex>
                <Field
                  type="email"
                  name="email"
                  id="email"
                  component={FormInput}
                  label="Email"
                  optionalText="Required"
                  size="large"
                  optional
                />
                <Field
                  type="text"
                  name="linkedinUrl"
                  id="linkedin"
                  component={FormInput}
                  label="LinkedIn profile URL"
                  placeholder="https://www.linkedin.com/in/..."
                  optionalText="Required"
                  size="large"
                  optional
                />
                <Field type="text" name="profileUrl" component={FormInput} label="Personal website" size="large" />
                <Field
                  containerProps={{
                    sx: {
                      label: {
                        fontSize: '16px',
                      },
                    },
                  }}
                  component={FormFile}
                  name="cvUrl"
                  label="Resume/CV"
                  accept=".pdf, .doc, .docx"
                  form={{
                    ...form,
                    setFieldValue: async (field, files) => {
                      const file = files[files.length - 1]?.content
                        ? files[files.length - 1]
                        : files.find((item) => item.content);
                      if (file) {
                        uploadFile(file, setFieldValue);
                        setFieldValue(field, [file]);
                      } else {
                        setFieldValue(field, []);
                      }
                    },
                  }}
                  field={{
                    id: 'cvUrl',
                    name: 'cvUrl',
                    value: values.cvUrl.length > 0 ? values.cvUrl : [],
                  }}
                  onChange={(e) => handleFileChange(e, setFieldValue)}
                />
                {resumeError && (
                  <Flex
                    alignItems="center"
                    sx={{ div: { margin: '0px', padding: '0px' } }}
                    minHeight="40px"
                    padding="0px 8px"
                    backgroundColor="red.20"
                  >
                    <FormError>
                      Upload a .pdf, .doc or .docx file up to 10MB. The filename can only contain letters, numbers,
                      hyphens (-), and underscores (_), and must be between 1 and 255 characters long.
                    </FormError>
                  </Flex>
                )}

                {showUSWorkAuthorization && (
                  <>
                    <Box data-testid="show-us-work-auth">
                      <FormLabel size="large" extra="Required">
                        Are you legally authorized to work in the United States?
                      </FormLabel>
                      <Field
                        name="usWorkAuthorization"
                        id="usWorkAuthorization"
                        component={() => (
                          <Select
                            placeholder="Select"
                            name="usWorkAuthorization"
                            id="usWorkAuthorization"
                            value={values.usWorkAuthorization}
                            options={[
                              { label: 'Yes', value: 'yes' },
                              { label: 'No', value: 'no' },
                            ]}
                            onChange={(e) => setFieldValue('usWorkAuthorization', e)}
                            labelExtra="Required"
                          />
                        )}
                      />
                    </Box>
                    <Box>
                      <FormLabel size="large" extra="Required">
                        Will you now or in the future require sponsorship or employment visa status (e.g. H-1B)
                      </FormLabel>
                      <Field
                        name="usVisaSponsorship"
                        id="usVisaSponsorship"
                        component={() => (
                          <Select
                            name="usVisaSponsorship"
                            id="usVisaSponsorship"
                            value={values.usVisaSponsorship}
                            placeholder="Select"
                            options={[
                              { label: 'Yes', value: 'yes' },
                              { label: 'No', value: 'no' },
                            ]}
                            onChange={(e) => setFieldValue('usVisaSponsorship', e)}
                            labelExtra="Required"
                          />
                        )}
                      />
                    </Box>
                  </>
                )}
                <Box>
                  <FormLabel size="large">Additional info</FormLabel>
                  <Text fontSize="1" mb="2" lineHeight="20px">
                    Tell us why you are a good fit, add a cover letter or anything else you want to share.
                  </Text>
                  <FormField
                    field={{ name: 'additionalInfo' }}
                    id="additionalInfo"
                    as="textarea"
                    name="additionalInfo"
                    value={values.additionalInfo}
                    form={form}
                    sx={{
                      fontFamily: 'inherit',
                    }}
                    onChange={(e) => setFieldValue('additionalInfo', e.target.value)}
                  />
                </Box>
                <Field
                  name="termsOfService"
                  component={(data) => (
                    <Box>
                      <Checkbox
                        label={
                          <span>
                            I have read and accept {network.name}&rsquo;s{' '}
                            <a href={privacyPolicyUrl} target="_blank">
                              Privacy Policy
                            </a>{' '}
                            and the{' '}
                            <a href="https://www.getro.com/terms" target="_blank">
                              Terms of Use
                            </a>{' '}
                            of this software.
                          </span>
                        }
                        name="termsOfService"
                        checked={values.termsOfService}
                        onChange={(e) => setFieldValue('termsOfService', e.target.checked)}
                        {...data}
                      />
                      {touched.termsOfService && errors.termsOfService ? (
                        <FormError>{errors.termsOfService}</FormError>
                      ) : null}
                    </Box>
                  )}
                />
                <Flex justifyContent="center">
                  <Button
                    display="flex"
                    justifyContent="center"
                    width={['100%', 'fit-content']}
                    loading={savingJob}
                    as="button"
                    type="submit"
                    size="large"
                    variant="primary"
                  >
                    Submit application
                  </Button>
                </Flex>
              </Flex>
            </>
          )}
          {savingError && (
            <Box data-testid="on-board-error" mt="16px">
              {savingErrorStatus === 403 ? (
                <Message
                  type="error"
                  content={
                    <>
                      Your application couldn&apos;t be submitted. Make sure you&apos;re not using any proxy or VPN and
                      try again. If the issue persists, contact us at{' '}
                      <Link href="mailto:support@getro.com" target="_blank" rel="noreferrer">
                        support@getro.com
                      </Link>
                      .
                    </>
                  }
                />
              ) : (
                <Message
                  type="error"
                  content={
                    <>
                      Something went wrong, please try again or contact us at{' '}
                      <Text as="a" href="mailto:support@getro.com" target="_blank" rel="noreferrer">
                        support@getro.com
                      </Text>
                      .
                    </>
                  }
                />
              )}
            </Box>
          )}
          {savingJobDone[jobId] && (
            <MessageBlock
              data-testid="on-board-complete"
              flex="1"
              type="success"
              title="We received your application"
              content="We’ll send your application to the right hiring lead to be reviewed."
            />
          )}

          <Text mt="32px" textAlign="center" fontSize="14px" color="text.subtle">
            To withdraw or update your application, email{' '}
            <Text as="a" color="inherit" href="mailto:applications@getro.com" target="_blank" rel="noreferrer">
              applications@getro.com
            </Text>
          </Text>
        </Form>
      )}
    </Formik>
  );
};

OnBoardApplication.propTypes = {
  network: NetworkSchema.isRequired,
};
